/* eslint-disable no-unreachable */
import { useState } from 'react';
import { useEffect } from 'react';
import { Button, Col, Row, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { FormGroup } from '../../../../Components/FormGroup/FormGroup';
import { Heading4 } from '../../../../Components/FormHeading';
import { handleActiveEventManagerChange } from '../../../../Redux/slices/event';
import { fetchWrapper } from '../../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../../_helpers/_methods';

function EventsManager() {
  const {
    register,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id, eventId } = useParams();
  const event = useSelector((state) => state.event.event);
  const hasEventCompleted = event?.completed;
  let mid = id || eventId;
  // const location = useLocation();
  // const isEdit = location?.pathname?.includes('edit_managers');
  const [otherExecutives, setOtherExecutives] = useState([]);
  const [selectedExecutives, setSelectedExecutives] = useState([]);
  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [activatedExecutive, setActivatedExecutive] = useState(null);

  useEffect(() => {
    const getExecutives = async () => {
      try {
        const response = await fetchWrapper.get(`/event/${mid}/executive/`);
        const results = response.data.results.map(({ is_active, executive }) => {
          if (is_active) {
            setActivatedExecutive(executive.id);
          }
          return {
            label: `${executive.fullname} (${executive.province?.title}) (${executive.phone})`,
            value: executive.id
          };
        });
        setSelectedExecutives(results);
      } catch (err) {
        console.log(err);
      }
    };
    getExecutives();
  }, []);

  useEffect(() => {
    const getOtherExecutives = async () => {
      try {
        const response = await fetchWrapper.get('/profile/executive/?limit=50');
        let selectedExecutivesIdList = selectedExecutives.map((exe) => exe.value);
        let results = response.data.results
          .filter((ex) => !selectedExecutivesIdList.includes(ex.id))
          .map((executive) => ({
            label: `${executive.fullname} (${executive.province?.title}) (${executive.phone})`,
            value: executive.id
          }));

        setOtherExecutives(results);
      } catch (err) {
        console.log(err);
      }
    };
    getOtherExecutives();
  }, [selectedExecutives]);

  const handleExecutiveChange = (element) => {
    setSelectedExecutive(element);
    setValue('executive', element);
  };

  const handleExecutiveActivation = (executive) => {
    setActivatedExecutive(executive);
    updateExecutiveList(executive);
  };

  const updateExecutiveList = async (activeExecutiveId) => {
    try {
      let executives = selectedExecutives.map((executive) => {
        return {
          executive: executive.value,
          is_active: activeExecutiveId === executive.value
        };
      });
      const response = await fetchWrapper.put(`/event/${mid}/executive/`, {
        executives: executives
      });
      const activeExecutive = response.data.find((executive) => executive.is_active);
      if (activeExecutive) {
        dispatch(handleActiveEventManagerChange(activeExecutive.executive));
      }
      toast.success(intl.formatMessage({ id: 'executiveUpdated' }));
    } catch (err) {
      console.log(err);
    }
  };
  const handleExecutiveRemove = async (executive) => {
    try {
      let newExecutives = selectedExecutives.filter((selected) => selected.value !== executive);
      setSelectedExecutives(newExecutives);
      newExecutives = newExecutives.map((executive) => {
        return {
          executive: executive.value,
          is_active: activatedExecutive === executive.value
        };
      });
      await fetchWrapper.put(`/event/${mid}/executive/`, {
        executives: newExecutives
      });
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddExecutive = async () => {
    try {
      const makeActiveManager = selectedExecutives.length === 0;
      const response = await fetchWrapper.post(`/event/${mid}/executive/`, {
        executive: selectedExecutive.value,
        is_active: makeActiveManager
      });
      let newExecutive = {
        label: `${response.data.executive.fullname} (${response.data.executive.province?.title}) (${response.data.executive.phone})`,
        value: response.data.executive.id
      };
      if (makeActiveManager) {
        setActivatedExecutive(selectedExecutive.value);
        dispatch(handleActiveEventManagerChange(response.data.executive));
      }
      setSelectedExecutives([newExecutive, ...selectedExecutives]);
      setSelectedExecutive(null);
      toast.success('Executive added successfully');
    } catch (err) {
      ErrorMessageHandler(err);
    }
  };

  return (
    <div className="card">
      <StyledEventsManagerDiv>
        <div className="card-header">
          <Heading4 className="text-primary">
            <FormattedMessage id="eventStep6.addEventManager" />
          </Heading4>
        </div>
        <div className="card-body">
          <Row>
            {!hasEventCompleted && (
              <Col md="4 d-flex align-items-end">
                <FormattedMessage id="please_select">
                  {(placeholder) => (
                    <FormGroup
                      key={selectedExecutive}
                      label={<FormattedMessage id="eventStep6.executive" />}
                      id="executive"
                      type="select"
                      register={register}
                      clearErrors={clearErrors}
                      options={otherExecutives}
                      placeholder={placeholder[0]}
                      onChange={handleExecutiveChange}
                      defaultValue={selectedExecutive}
                      error={errors}
                    />
                  )}
                </FormattedMessage>
                {selectedExecutive && (
                  <Button className="ms-4" onClick={handleAddExecutive}>
                    <FormattedMessage id="button.add" />
                  </Button>
                )}
              </Col>
            )}
            <Col className="mt-4" md="12">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>
                      <FormattedMessage id="sn" />
                    </th>
                    <th>
                      <FormattedMessage id="username" />
                    </th>
                    <th>
                      <FormattedMessage id="activate" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedExecutives.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center">
                        <FormattedMessage id="no_executive_selected" />
                      </td>
                    </tr>
                  ) : (
                    selectedExecutives.map((executive, index) => (
                      <tr
                        md="12"
                        key={executive.value}
                        className={`${executive.value === activatedExecutive ? 'activated' : ''}`}>
                        <td>{index + 1}</td>
                        <td>{executive.label}</td>
                        <td>
                          {executive.value === activatedExecutive ? null : (
                            <>
                              <Button onClick={() => handleExecutiveActivation(executive.value)}>
                                <FormattedMessage id="button.activate" />
                              </Button>

                              <Button
                                variant="outline-danger"
                                className="ms-2"
                                onClick={() => handleExecutiveRemove(executive.value)}>
                                <FormattedMessage id="button.remove" />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </div>
      </StyledEventsManagerDiv>
    </div>
  );
}

const StyledEventsManagerDiv = styled.div`
  .activated {
    background-color: #bbe2ff !important;
  }
`;
export default EventsManager;
